/**
 * ResetPassword — F10 T10.11.
 *
 * Konsumuje token wystawiony przez forgot-password. URL z maila:
 *   `<slug>.app.<base>/reset-password?token=<hex64>&slug=<slug>`
 * Token + slug czytamy z `?...` — backend wymaga obu zeby trafic w odpowiednia
 * tenant DB.
 *
 * Walidacje frontowe (best-effort, backend wymusza twardo):
 *   - dlugosc >= 10
 *   - zawiera cyfre
 *   - zawiera duza litere
 *   - powtorz haslo musi sie zgadzac
 * Te checki to UX — backend i tak waliduje policy. Jezeli backend zwroci
 * PASSWORD_POLICY_VIOLATION → wyswietlamy szczegoly z details[].
 *
 * Stany:
 *   - 'idle' — formularz aktywny.
 *   - 'submitting' — request w locie.
 *   - 'success' — backend zaakceptowal, navigate '/login' z toast'em.
 *   - 'expired' — backend zwrocil 410 INVALID_OR_EXPIRED → ekran „Link wygasl"
 *     z linkiem do forgot-password.
 *
 * Inne errory (PASSWORD_RECENTLY_USED, INTERNAL_ERROR) → toast i powrot do idle.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

type State = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'expired' };

interface BackendError {
  error?: string;
  details?: string[] | Record<string, string[]>;
}

function extractError(err: unknown): { status: number | null; data: BackendError | null } {
  const e = err as { response?: { status?: number; data?: BackendError } };
  return {
    status: e.response?.status ?? null,
    data: e.response?.data ?? null,
  };
}

function policyErrorsToMessages(details: string[] | undefined, t: (k: string) => string): string[] {
  if (!details) return [];
  return details.map((code) => {
    switch (code) {
      case 'TOO_SHORT':
        return t('auth.reset.policy.tooShort');
      case 'TOO_LONG':
        return t('auth.reset.policy.tooLong');
      case 'NO_DIGIT':
        return t('auth.reset.policy.noDigit');
      case 'NO_UPPER':
        return t('auth.reset.policy.noUpper');
      case 'NO_LOWER':
        return t('auth.reset.policy.noLower');
      case 'NO_SPECIAL':
        return t('auth.reset.policy.noSpecial');
      default:
        return code;
    }
  });
}

export function ResetPassword() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') ?? '';
  const slug = searchParams.get('slug') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [policyErrors, setPolicyErrors] = useState<string[]>([]);
  const [mismatchError, setMismatchError] = useState(false);

  // Token/slug missing -> traktujemy jako wygasly (anti-enumeration spojnie z backendem).
  useEffect(() => {
    if (!token || !slug) {
      setState({ kind: 'expired' });
    }
  }, [token, slug]);

  const submitting = state.kind === 'submitting';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPolicyErrors([]);
    setMismatchError(false);

    if (password !== confirm) {
      setMismatchError(true);
      return;
    }

    setState({ kind: 'submitting' });

    try {
      await api.post('/auth/reset-password', {
        token,
        newPassword: password,
        tenantSlug: slug,
      });
      toast.show({
        variant: 'success',
        title: t('auth.reset.successTitle'),
        description: t('auth.reset.successDescription'),
      });
      navigate('/login', { replace: true });
    } catch (err) {
      const { status, data } = extractError(err);
      if (status === 410) {
        setState({ kind: 'expired' });
        return;
      }
      if (status === 400 && data?.error === 'PASSWORD_POLICY_VIOLATION') {
        const details = Array.isArray(data.details) ? data.details : [];
        setPolicyErrors(policyErrorsToMessages(details, t));
        setState({ kind: 'idle' });
        return;
      }
      if (status === 400 && data?.error === 'PASSWORD_RECENTLY_USED') {
        toast.show({
          variant: 'warning',
          title: t('auth.reset.errors.recentlyUsedTitle'),
          description: t('auth.reset.errors.recentlyUsedDescription'),
        });
        setState({ kind: 'idle' });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('auth.reset.errors.genericTitle'),
        description: t('auth.reset.errors.genericDescription'),
      });
      setState({ kind: 'idle' });
    }
  };

  if (state.kind === 'expired') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
        <div
          className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl"
          data-testid="reset-password-expired"
        >
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-text-primary">{t('auth.reset.expiredTitle')}</h1>
            <p className="mt-3 text-sm text-text-tertiary">{t('auth.reset.expiredDescription')}</p>
          </div>
          <div className="mt-8 flex flex-col gap-3">
            <Link to="/forgot-password">
              <Button className="w-full">{t('auth.reset.requestNewLink')}</Button>
            </Link>
            <Link to="/login">
              <Button variant="secondary" className="w-full">
                {t('auth.reset.backToLogin')}
              </Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
      <div className="w-full max-w-md rounded-2xl border border-border-secondary bg-bg-elevated p-8 shadow-xl">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-text-primary">{t('auth.reset.title')}</h1>
          <p className="mt-2 text-sm text-text-tertiary">{t('auth.reset.subtitle')}</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input
            label={t('auth.reset.passwordLabel')}
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            data-testid="reset-password-new"
          />
          <Input
            label={t('auth.reset.confirmLabel')}
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
            data-testid="reset-password-confirm"
          />

          <p className="text-xs text-text-tertiary">{t('auth.reset.policyHint')}</p>

          {mismatchError && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="reset-password-mismatch"
            >
              {t('auth.reset.errors.mismatch')}
            </div>
          )}

          {policyErrors.length > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
              data-testid="reset-password-policy-errors"
            >
              <ul className="list-inside list-disc space-y-1">
                {policyErrors.map((msg) => (
                  <li key={msg}>{msg}</li>
                ))}
              </ul>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            loading={submitting}
            data-testid="reset-password-submit"
          >
            {submitting ? t('auth.reset.submitting') : t('auth.reset.submit')}
          </Button>
        </form>
      </div>
    </main>
  );
}
